import React from "react";
import { Link } from "react-router-dom";
import { useTeamLookup } from "../lib/teamLookup";
import { assetUrl } from "../lib/api";
import { TEAM_INFO } from "../lib/teamMap";

const SIZE_CLASS = {
    xs: "h-4 w-4 text-[8px]",
    box: "h-10 w-10 text-sm",
    sm: "h-6 w-6 text-[10px]",
    md: "h-8 w-8 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-20 w-20 text-2xl",
    strip: "h-5 w-5 text-[7px] sm:h-16 sm:w-16 sm:text-xl",
    stripfit: "h-full w-full text-[8px]",
    "2xl": "h-28 w-28 text-3xl",
};

const LetterBadge = ({ team, sizeClass, bgStyle, showBorder, inset }) => {
    const hasBg = !!bgStyle.backgroundColor;
    // Only tall (portrait) logos — e.g. the Toronto Sceptres crest — get shrunk so
    // they sit inside the plate instead of touching the top/bottom edges. Square /
    // round / wide logos render at full size (unchanged). A ref callback handles
    // both fresh loads AND already-cached images (onLoad won't refire for cache).
    const [portrait, setPortrait] = React.useState(false);
    const imgRef = React.useCallback((node) => {
        if (!node) return;
        const check = () => {
            if (node.naturalWidth && node.naturalHeight) {
                setPortrait(node.naturalHeight > node.naturalWidth * 1.25);
            }
        };
        if (node.complete) check();
        else node.addEventListener("load", check, { once: true });
    }, []);
    const shrink = inset && portrait;
    // Manual per-team size (admin) takes precedence; otherwise fall back to the
    // auto portrait-shrink so tall logos aren't broken out of the box. A global
    // CSS var (--logo-scale, set from settings) multiplies everything.
    const perTeam = (team.logo_scale ?? 100) / 100;
    const effScale = perTeam !== 1 ? perTeam : (shrink ? 0.7 : 1);
    return (
        <span
            className={`${sizeClass} rounded-md flex items-center justify-center font-heading font-black shrink-0 overflow-hidden ${showBorder ? "border border-white/10" : ""}`}
            style={{ ...bgStyle, opacity: team.logo_opacity ?? 1 }}
        >
            {team.logo_url ? (
                <img
                    ref={imgRef}
                    src={assetUrl(team.logo_url)}
                    alt={team.name}
                    className="w-full aspect-square object-contain"
                    style={{ transform: `scale(calc(var(--logo-scale, 1) * ${effScale}))` }}
                />
            ) : (
                <span className={hasBg ? "" : "text-white/80"}>{team.abbr}</span>
            )}
        </span>
    );
};

// Decide the chip background behind a team's logo/abbr.
//  logo_bg: auto | transparent | white | team
//  - auto → NCAA & custom (non-PWHL) teams render transparent so dark logos stay
//           visible; PWHL renders its colour only when `solid` is requested.
function resolveBadgeBg(team, { solid, nonPwhl }) {
    const lb = team.logo_bg || "auto";
    if (lb === "transparent") return { bgStyle: {}, showBorder: false };
    if (lb === "white") return { bgStyle: { backgroundColor: "#FFFFFF" }, showBorder: true };
    if (lb === "team") return { bgStyle: { backgroundColor: team.primary_color, color: team.text_color || "#fff" }, showBorder: true };
    if (nonPwhl) return { bgStyle: {}, showBorder: false };
    if (solid) return { bgStyle: { backgroundColor: team.primary_color, color: team.text_color || "#fff" }, showBorder: true };
    return { bgStyle: {}, showBorder: false };
}

export const TeamBadge = ({ teamId, size = "sm", showName = false, asLink = true, solid = false }) => {
    const { any: anyTeam } = useTeamLookup();
    const t = anyTeam(teamId);
    // Cap abbr to 3 chars so unknown teams (e.g. NCAA schools without an override)
    // never bleed into the badge as cropped text.
    const safeAbbr = (t.abbr || "").slice(0, 3).toUpperCase() || (teamId || "").slice(0, 3).toUpperCase();
    const team = { ...t, abbr: safeAbbr };
    const sizeClass = SIZE_CLASS[size] ?? SIZE_CLASS.sm;
    const nonPwhl = !TEAM_INFO[teamId];
    const { bgStyle, showBorder } = resolveBadgeBg(team, { solid, nonPwhl });
    const inner = (
        <>
            <LetterBadge team={team} sizeClass={sizeClass} bgStyle={bgStyle} showBorder={showBorder} inset={size === "stripfit"} />
            {showName && (
                <span className="text-sm font-semibold text-white group-hover:text-[#9D4CDD] transition-colors">
                    {team.name}
                </span>
            )}
        </>
    );
    if (!asLink) {
        return (
            <span data-testid={`team-badge-${teamId}`} className="inline-flex items-center gap-2">
                {inner}
            </span>
        );
    }
    // Only link to /teams/{id} for actual PWHL teams. NCAA school names
    // would 404 the TeamDetail page — render as a non-link wrapper instead.
    const isLinkable = !!TEAM_INFO[teamId];
    if (!isLinkable) {
        return (
            <span data-testid={`team-badge-${teamId}`} className="inline-flex items-center gap-2">
                {inner}
            </span>
        );
    }
    return (
        <Link
            to={`/teams/${teamId}`}
            data-testid={`team-link-${teamId}`}
            className="inline-flex items-center gap-2 group"
        >
            {inner}
        </Link>
    );
};

export default TeamBadge;
